import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * 画面の言葉づかい。日本語と英語を切り替えられるようにしてある (msc-check と同じ)。
 * 仕入先名・車名・グレードといった台帳そのものの中身は、原本と突き合わせるための
 * 情報なので英語表示でも日本語のまま出す。ここで訳すのは画面の部品だけ。
 */
const DICT = {
  ja: {
    appName: '在庫管理',
    dashboard: 'ダッシュボード',
    stock: '在庫',
    sold: '売却済み',
    import: '取り込み',
    history: '取り込み履歴',
    logout: 'ログアウト',
    login: 'ログイン',
    password: 'パスワード',
    loading: '読み込み中…',
    noResults: '該当する車両はありません',
    search: '車台番号・車名・グレード・仕入先で検索',
    filters: '絞り込み',
    clear: '条件をクリア',
    all: 'すべて',
    maker: 'メーカー',
    model: '車名',
    grade: 'グレード',
    chassis: '車台番号',
    color: '色',
    year: '年式',
    supplier: '仕入先 (オークション)',
    lotNo: '出品番号',
    purchaseDate: '購入日',
    purchasePrice: '仕入価格',
    condition: '状態',
    flood: '水没',
    normal: '通常',
    parts: '部品',
    status: '状況',
    inStock: '在庫',
    soldStatus: '売却済み',
    soldDate: '在庫から消えた日',
    salePrice: '売却価格',
    saleDate: '売却日',
    buyer: '販売先',
    saleNote: '備考',
    profit: '粗利',
    count: '台数',
    total: '合計',
    division: '帳簿',
    vehicles: '台',
    save: '保存',
    saved: '保存しました',
    cancel: 'キャンセル',
    edit: '編集',
    detail: '詳細',
    back: '戻る',
    timeline: '履歴',
    page: 'ページ',
    prev: '前へ',
    next: '次へ',
    sortBy: '並び順',
    groupBy: 'グループ',
    byMaker: 'メーカー別',
    byModel: '車名別',
    bySupplier: '仕入先別',
    byColor: '色別',
    none: 'なし',
    inStockValue: '在庫金額 (仕入)',
    soldValue: '売却済み (仕入)',
    revenue: '売上 (入力済み)',
    saleMissing: '売却価格が未入力',
    lastImport: '最終取り込み',
    snapshotDate: '在庫リストの基準日',
    uploadTitle: '在庫リストを取り込む',
    uploadHint: 'ザイコリストの .xlsx を選んでください。前回との差分を確認してから確定します。',
    chooseFile: 'ファイルを選ぶ',
    checkDiff: '差分を確認',
    commit: 'この内容で取り込む',
    committing: '取り込み中…',
    added: '新規入庫',
    soldDiff: '売却 (リストから消えた)',
    returned: '在庫に戻った',
    updated: '内容が変わった',
    unchanged: '変化なし',
    verify: '検算',
    verifyOk: 'Excel の合計行と一致',
    verifyNg: 'Excel の合計行と不一致',
    warnings: '注意',
    note: 'メモ',
    filename: 'ファイル名',
    uploadedAt: '取り込み日時',
    download: '元ファイル',
    purchasedByMonth: '月別の仕入',
    soldByMonth: '月別の売却',
    oldest: '在庫期間が長い車両',
    markSold: '売却にする',
    markInStock: '在庫に戻す',
    manualStatusHint: '通常は取り込みが自動で切り替えます。',
    saleMissingOnly: '売却価格が未入力のものだけ',
    notEntered: '未入力',
    language: 'English',
  },
  en: {
    appName: 'Stock',
    dashboard: 'Dashboard',
    stock: 'In stock',
    sold: 'Sold',
    import: 'Import',
    history: 'Import history',
    logout: 'Log out',
    login: 'Log in',
    password: 'Password',
    loading: 'Loading…',
    noResults: 'No vehicles match',
    search: 'Search chassis no., model, grade or auction',
    filters: 'Filters',
    clear: 'Clear filters',
    all: 'All',
    maker: 'Maker',
    model: 'Model',
    grade: 'Grade',
    chassis: 'Chassis no.',
    color: 'Colour',
    year: 'Year',
    supplier: 'Auction / supplier',
    lotNo: 'Lot no.',
    purchaseDate: 'Purchased',
    purchasePrice: 'Cost',
    condition: 'Condition',
    flood: 'Flood',
    normal: 'Normal',
    parts: 'Parts',
    status: 'Status',
    inStock: 'In stock',
    soldStatus: 'Sold',
    soldDate: 'Left the list on',
    salePrice: 'Sale price',
    saleDate: 'Sale date',
    buyer: 'Buyer',
    saleNote: 'Note',
    profit: 'Profit',
    count: 'Units',
    total: 'Total',
    division: 'Book',
    vehicles: '',
    save: 'Save',
    saved: 'Saved',
    cancel: 'Cancel',
    edit: 'Edit',
    detail: 'Detail',
    back: 'Back',
    timeline: 'History',
    page: 'Page',
    prev: 'Prev',
    next: 'Next',
    sortBy: 'Sort',
    groupBy: 'Group',
    byMaker: 'By maker',
    byModel: 'By model',
    bySupplier: 'By auction',
    byColor: 'By colour',
    none: 'None',
    inStockValue: 'Stock value (cost)',
    soldValue: 'Sold (cost)',
    revenue: 'Revenue (entered)',
    saleMissing: 'Sale price missing',
    lastImport: 'Last import',
    snapshotDate: 'List date',
    uploadTitle: 'Import a stock list',
    uploadHint: 'Pick the ザイコリスト .xlsx. You will see the diff before anything is saved.',
    chooseFile: 'Choose file',
    checkDiff: 'Check diff',
    commit: 'Apply this import',
    committing: 'Importing…',
    added: 'New arrivals',
    soldDiff: 'Sold (gone from list)',
    returned: 'Back in stock',
    updated: 'Details changed',
    unchanged: 'Unchanged',
    verify: 'Check',
    verifyOk: 'Matches the sheet totals',
    verifyNg: 'Does NOT match the sheet totals',
    warnings: 'Warnings',
    note: 'Note',
    filename: 'File',
    uploadedAt: 'Imported at',
    download: 'Original file',
    purchasedByMonth: 'Purchases by month',
    soldByMonth: 'Sales by month',
    oldest: 'Longest in stock',
    markSold: 'Mark as sold',
    markInStock: 'Put back in stock',
    manualStatusHint: 'Normally the import does this for you.',
    saleMissingOnly: 'Only those missing a sale price',
    notEntered: 'Not entered',
    language: '日本語',
  },
} as const;

export type Lang = keyof typeof DICT;
export type TextKey = keyof (typeof DICT)['ja'];

interface I18nValue {
  lang: Lang;
  t: (key: TextKey) => string;
  toggle: () => void;
}

const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = 'msc-stock.lang';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'en' || saved === 'ja' ? saved : 'ja';
  });

  const toggle = useCallback(() => {
    setLang((prev) => {
      const next: Lang = prev === 'ja' ? 'en' : 'ja';
      localStorage.setItem(STORAGE_KEY, next);
      document.documentElement.lang = next;
      return next;
    });
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ lang, toggle, t: (key) => DICT[lang][key] ?? String(key) }),
    [lang, toggle],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('I18nProvider の外で useI18n が呼ばれました');
  return ctx;
}

/** 金額。日本語でも英語でも円で出す (台帳が円なので通貨は変えない)。 */
export function yen(n: number | null | undefined): string {
  if (n == null) return '—';
  return '¥' + Math.round(n).toLocaleString('en-US');
}

/** 日付。ロケールに合わせて表記だけ変える。 */
export function formatDate(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'ja' ? 'ja-JP' : 'en-GB', { timeZone: 'UTC' });
}
